import { DateTime } from "luxon";
import React, { useCallback, useEffect, useState } from "react";
import { VelavuAlert } from "velavu-js-api";
import { useTranslation } from "react-i18next";
import IconCloseLeft from "../../dynamicicons/icon-close-left";
import IconDelete from "../../dynamicicons/icon-delete";
import IconMute from "../../dynamicicons/icon-mute";
import IconVelavuAlerts from "../../dynamicicons/icon-velavu-alerts";
import AlertCounter from "../../elements/alert-counter";
import Divider from "../../elements/divider";
import VelavuIconButton from "../../elements/velavu-icon-button";
import VelavuMenu, {
	VelavuMenuItem,
	VelavuOverflowButton,
} from "../../elements/velavu-menu";
import AlertItem, { AlertsMenu } from "./alert-item";
import * as styles from "./alerts.module.scss";
import { NoAlertsIcon } from "../../img/icons/icons";
import whitelabel from "../../whitelabel";
import { classArr } from "../../helper/style-helper";

interface AlertsProps {
	show?: boolean;
	onClose: VoidFunction;

	alerts: VelavuAlert[] | undefined;
	alertCount: number;
	readAlertIDs: string[];
	onDismiss: (alert: VelavuAlert) => void;
	onDelete?: (alert: VelavuAlert) => void;

	onDismissAll: VoidFunction;
	onDeleteAll?: VoidFunction;
	onOpenAlertsSettings: VoidFunction;
}

function NoAlerts() {
	const { t } = useTranslation();
	return (
		<div className={styles.emptyContent}>
			<NoAlertsIcon />
			<span className={styles.heading}>{t("alerts.no_alerts")}</span>
			<span className={styles.info}>{t("alerts.up_to_date")}</span>
		</div>
	);
}

const TIME_UPDATE_INTERVAL = 30 * 1000;

export default function Alerts(props: AlertsProps) {
	const { t } = useTranslation();
	const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
	const openMenu = useCallback(
		(event: React.MouseEvent<HTMLElement>) => {
			setMenuAnchor(event.currentTarget);
		},
		[setMenuAnchor],
	);
	const closeMenu = useCallback(() => {
		setMenuAnchor(null);
	}, [setMenuAnchor]);

	const [itemMenu, setItemMenu] = useState<AlertsMenu | null>(null);
	const openItemMenu = useCallback(
		(event: AlertsMenu) => {
			setItemMenu(event);
		},
		[setItemMenu],
	);
	const closeItemMenu = useCallback(() => {
		setItemMenu(null);
	}, [setItemMenu]);

	const {
		onDismissAll: propsOnDismissAll,
		onDeleteAll: propsOnDeleteAll,
		onOpenAlertsSettings: propsOnOpenAlertsSettings,
	} = props;
	const dismissAll = useCallback(() => {
		propsOnDismissAll();
		closeMenu();
	}, [propsOnDismissAll, closeMenu]);
	const deleteAll = useCallback(() => {
		propsOnDeleteAll?.();
		closeMenu();
	}, [propsOnDeleteAll, closeMenu]);
	const openSettings = useCallback(() => {
		propsOnOpenAlertsSettings();
		closeMenu();
	}, [propsOnOpenAlertsSettings, closeMenu]);

	//Update the base time every minute
	const [baseTime, setBaseTime] = useState(DateTime.now());
	useEffect(() => {
		const intervalID = setInterval(
			() => setBaseTime(DateTime.now()),
			TIME_UPDATE_INTERVAL,
		);
		return () => clearInterval(intervalID);
	}, [setBaseTime]);

	return (
		<>
			<VelavuMenu
				open={!!menuAnchor}
				onClose={closeMenu}
				refPosition={menuAnchor}
			>
				{props.alertCount > 0 && (
					<VelavuMenuItem
						icon={(size, color) => (
							<IconMute size={size} color={color} />
						)}
						label={t("alerts.dismiss_all")}
						onClick={dismissAll}
					/>
				)}
				{!!props.alerts?.length && props.onDeleteAll && (
					<VelavuMenuItem
						icon={(size, color) => (
							<IconDelete size={size} color={color} />
						)}
						label={t("alerts.delete_all")}
						onClick={deleteAll}
					/>
				)}
				<VelavuMenuItem
					icon={(size, color) => (
						<IconVelavuAlerts size={size} color={color} />
					)}
					label={t("alerts.alerts_settings")}
					onClick={openSettings}
				/>
			</VelavuMenu>

			<VelavuMenu
				open={!!itemMenu}
				onClose={closeItemMenu}
				refPosition={itemMenu ? itemMenu.anchor : null}
			>
				{itemMenu?.unread && (
					<VelavuMenuItem
						icon={(size, color) => (
							<IconMute size={size} color={color} />
						)}
						label={t("alerts.dismiss")}
						onClick={
							itemMenu
								? () => {
										closeItemMenu();
										props.onDismiss(itemMenu.alert);
									}
								: undefined
						}
					/>
				)}
				{props.onDelete && (
					<VelavuMenuItem
						icon={(size, color) => (
							<IconDelete size={size} color={color} />
						)}
						label={t("alerts.delete")}
						onClick={
							itemMenu
								? () => {
										closeItemMenu();
										props.onDelete?.(itemMenu.alert);
									}
								: undefined
						}
					/>
				)}
			</VelavuMenu>

			<div
				className={classArr(
					styles.container,
					props.show
						? styles.containerExpand
						: styles.containerCollapse,
				)}
			>
				<div className={styles.header}>
					<div className={styles.icon}>
						<IconVelavuAlerts
							color={whitelabel.palette.secondary900}
						/>
					</div>
					<span className={styles.title}>{t("alerts.title")}</span>
					{props.alertCount > 0 && (
						<AlertCounter count={props.alertCount} />
					)}

					<div className={styles.spacer} />

					<VelavuIconButton
						tooltip={t("alerts.close")}
						onClick={props.onClose}
					>
						<IconCloseLeft
							color={whitelabel.palette.secondary700}
						/>
					</VelavuIconButton>
					<VelavuOverflowButton
						onClick={openMenu}
						open={!!menuAnchor}
					/>
				</div>

				<Divider />

				{props.alerts !== undefined ? (
					props.alerts.length > 0 ? (
						<div className={styles.content}>
							{props.alerts.map((alert) => {
								const unread = !props.readAlertIDs.find(
									(readAlertID) => readAlertID === alert.id,
								);

								return (
									<AlertItem
										key={alert.id}
										alert={alert}
										baseTime={baseTime}
										unread={unread}
										onOpenMenu={openItemMenu}
										menuOpen={
											itemMenu?.alert.id === alert.id
										}
									/>
								);
							})}
						</div>
					) : (
						<NoAlerts />
					)
				) : (
					<div className={styles.contentEmpty}>
						<span>{t("alerts.please_wait")}</span>
					</div>
				)}
			</div>
		</>
	);
}
